// src/pages/entit/tnm.js
import React, { useMemo, useState, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Container, Row, Col, Form, Table } from "react-bootstrap";
import tnmData from "../../assets/data/tnm.json";
import "../../App.css";

// ✨ Import DATENSTAND + export utility
import { DATENSTAND } from "../../config/constants";
import { TNM_COLORS } from "../../config/colors";
import { exportToExcel } from "../../utils/exportExcel";

// ⭐ EXCLUDED ENTITIES
const EXCLUDED_ENTITS = [
  "Chronische lymphatische Leukämie (CLL/SLL)",
  "Multiples Myelom",
  "Diffuses großzelliges B-Zell-Lymphom (DLBCL)",
];

// --- Category order + colors ---
const CATEGORY_ORDER = ["is", "a", "0", "1", "2", "3", "4", "X"];

// --- Bar width ---
const BAR_SIZE = 70;

// --- Formatters ---
const nfInt = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });
const nfPct = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const fmtInt = (v) => (v == null ? "–" : nfInt.format(v));
const fmtPct = (v) => (v == null || Number.isNaN(v) ? "–" : nfPct.format(v));

// Sorting helper
const uniqueSorted = (arr) =>
  [...new Set(arr)].sort((a, b) => {
    const an = Number(a);
    const bn = Number(b);
    if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
    return String(a).localeCompare(String(b), "de");
  });

// --- Friendly labels ---
const COMPONENT_LABELS = {
  T: "Tumorgröße",
  N: "Lymphknotenstatus",
  M: "Fernmetastasen",
};

// --- Table categories ---
const TABLE_CATS = {
  T: ["is", "a", "0", "1", "2", "3", "4", "X"],
  N: ["0", "1", "2", "3", "X"],
  M: ["0", "1", "X"],
};

export default function Tnm() {
  // --- Dropdowns ---
  const years = useMemo(() => uniqueSorted(tnmData.map((d) => d.diayear)), []);

  // 🔹 all entities in dropdown (no filter here)
  const entits = useMemo(
    () => uniqueSorted(tnmData.map((d) => d.entit)),
    []
  );

  // 🔹 default to latest year
  const [selectedYear, setSelectedYear] = useState(
    String(years[years.length - 1] ?? "")
  );
  const [selectedEntit, setSelectedEntit] = useState(entits[0] ?? "");

  const isExcluded = EXCLUDED_ENTITS.includes(selectedEntit);

  // --- Build 100% stacked data ---
  const buildStacked = useCallback(
    (sex) => {
      const components = ["T", "N", "M"];
      const rows = tnmData.filter(
        (d) =>
          String(d.diayear) === String(selectedYear) &&
          d.entit === selectedEntit &&
          d.SEX === sex &&
          ["T", "N", "M"].includes(d.TNM_component)
      );

      const byComp = {};
      rows.forEach((r) => {
        const comp = r.TNM_component;
        if (!byComp[comp]) {
          byComp[comp] = { comp };
          CATEGORY_ORDER.forEach((c) => (byComp[comp][c] = 0));
        }
        const cat = String(r.category);
        const pct =
          Number(r.pct ?? (r.total ? (r.n / r.total) * 100 : 0)) || 0;
        if (CATEGORY_ORDER.includes(cat)) byComp[comp][cat] += pct;
      });

      components.forEach((comp) => {
        if (!byComp[comp]) {
          byComp[comp] = { comp };
          CATEGORY_ORDER.forEach((c) => (byComp[comp][c] = 0));
        }
      });

      return components.map((c) => byComp[c]);
    },
    [selectedYear, selectedEntit]
  );

  const dataMen = useMemo(() => buildStacked("M"), [buildStacked]);
  const dataWomen = useMemo(() => buildStacked("W"), [buildStacked]);

  // --- Tooltip ---
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const items = payload
      .filter((p) => (p?.value ?? 0) > 0)
      .sort(
        (a, b) => CATEGORY_ORDER.indexOf(a.name) - CATEGORY_ORDER.indexOf(b.name)
      );

    return (
      <div
        className="bg-white text-black p-2 rounded"
        style={{ fontSize: 18, lineHeight: 1.3 }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {items.map((seg, i) => (
            <li
              key={i}
              style={{
                color: seg.name === "X" ? "#000000" : seg.color,
              }}
            >
              {label}
              {seg.name}: {fmtPct(seg.value)}%
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const LegendTNM = () => (
    <div style={{ fontSize: 12, color: "#555", margin: "6px 0 10px" }}>
      {CATEGORY_ORDER.map((c) => (
        <span key={c} style={{ marginRight: 16 }}>
          <span
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              borderRadius: 3,
              backgroundColor: TNM_COLORS[c],
            }}
          />
          <span style={{ marginLeft: 6 }}>{c}</span>
        </span>
      ))}
    </div>
  );

  const SexChart = ({ title, data, stackId }) => (
    <div className="h-100">
      <h5 className="mb-2">{title}</h5>
      <LegendTNM />
      <ResponsiveContainer width="100%" height={420}>
        <BarChart data={data} barCategoryGap="40%">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="comp" />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 20, 40, 60, 80, 100]}
            allowDecimals={false}
            unit="%"
          />
          <Tooltip content={<CustomTooltip />} />
          {CATEGORY_ORDER.map((cat) => (
            <Bar
              key={cat}
              dataKey={cat}
              name={cat}
              stackId={stackId}
              fill={TNM_COLORS[cat]}
              barSize={BAR_SIZE}
              stroke="#ffffff"
              strokeWidth={1}
              strokeLinejoin="miter"
              strokeLinecap="butt"
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  // --- Table build ---
  const filtered = useMemo(
    () =>
      tnmData.filter(
        (d) =>
          String(d.diayear) === String(selectedYear) &&
          d.entit === selectedEntit
      ),
    [selectedYear, selectedEntit]
  );

  const tableRows = useMemo(() => {
    const comps = ["T", "N", "M"];
    const rows = [];

    comps.forEach((comp) => {
      const labelComp = COMPONENT_LABELS[comp] || comp;

      const maleComp = filtered.filter(
        (d) => d.SEX === "M" && d.TNM_component === comp
      );
      const femaleComp = filtered.filter(
        (d) => d.SEX === "W" && d.TNM_component === comp
      );

      const totalM =
        (maleComp.length &&
          (Number(maleComp.find((r) => r.total != null)?.total) ||
            maleComp.reduce((s, r) => s + (Number(r.n) || 0), 0))) ||
        0;
      const totalW =
        (femaleComp.length &&
          (Number(femaleComp.find((r) => r.total != null)?.total) ||
            femaleComp.reduce((s, r) => s + (Number(r.n) || 0), 0))) ||
        0;
      const denom = totalM + totalW;

      rows.push({ section: labelComp });

const catsForComp = TABLE_CATS[comp] || CATEGORY_ORDER;

      catsForComp.forEach((cat) => {
        const mRec = maleComp.find((r) => String(r.category) === cat);
        const wRec = femaleComp.find((r) => String(r.category) === cat);

        const n_m = mRec ? Number(mRec.n) : null;
        const n_w = wRec ? Number(wRec.n) : null;

        const pct_m =
          mRec && mRec.pct != null
            ? Number(mRec.pct)
            : mRec && totalM > 0
            ? (Number(mRec.n) / totalM) * 100
            : null;

        const pct_w =
          wRec && wRec.pct != null
            ? Number(wRec.pct)
            : wRec && totalW > 0
            ? (Number(wRec.n) / totalW) * 100
            : null;

        const n_g = (n_m || 0) + (n_w || 0);
        const pct_g = denom > 0 ? (n_g / denom) * 100 : null;

        // 🔹 skip completely empty categories (no male and no female record)
        const hasData =
          (n_m != null && !Number.isNaN(n_m)) ||
          (n_w != null && !Number.isNaN(n_w));

        if (!hasData) return; // do not add this row

        rows.push({
          label: `${comp}${cat}`,
          n_g,
          n_m,
          n_w,
          pct_g,
          pct_m,
          pct_w,
        });
      });
    });

    return rows;
  }, [filtered]);

  const handleExport = () => {
    if (isExcluded) {
      return;
    }

  const excelData = tableRows
    .filter((r) => !r.section)
    .map((r) => ({
      Kategorie: r.label,
      Gesamt: r.n_g ?? "–",
      Männer: r.n_m ?? "–",
      Frauen: r.n_w ?? "–",
      "Gesamt (%)": fmtPct(r.pct_g),
      "Männer (%)": fmtPct(r.pct_m),
      "Frauen (%)": fmtPct(r.pct_w),
    }));

  exportToExcel({
    filename: "tnm",
    sheetName: "TNM-Daten",
    data: excelData,
    metadata: [
      ["TNM Status"],
      ["Krebsregister Sachsen-Anhalt https://www.kr-lsa.de"],
      [`Datenstand: ${DATENSTAND}`],
      [`Export am: ${new Date().toLocaleString("de-DE")}`],
    ],
  });
};


  return (
    <Container className="mt-4">
      <h2 className="section-title">TNM-Status</h2>
      <div className="section-title-underline"></div>

      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
         Die meisten soliden Krebserkrankungen können nach dem TNM-System klassifiziert werden. 
         Dabei werden der <strong>T</strong>umor (Ausdehnung und Verhalten des Primärtumors), die Lymphknoten
          (<strong>N</strong>odus, Fehlen oder Vorhandensein von regionären Lymphknotenmetastasen)
          und <strong>M</strong>etastasen (Fehlen oder Vorhandensein von Fernmetastasen) beurteilt.
        </p>
      </div>

      {/* ⭐ EXPORT BUTTON */}
      <button onClick={handleExport} className="top-btn mb-3"  disabled={isExcluded}>
        📥 Daten als Excel exportieren
      </button>

      {/* Dropdowns */}
      <div
        className="d-flex align-items-center gap-3 mb-3"
        style={{ maxWidth: 720 }}
      >
        <Form.Group controlId="cancerSelect" className="flex-fill">
          <Form.Label>Krebs-Auswahl:</Form.Label>
          <Form.Select
            value={selectedEntit}
            onChange={(e) => setSelectedEntit(e.target.value)}
          >
            {entits.map((ent) => (
              <option key={ent} value={ent}>
                {ent}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="yearSelect" className="flex-fill">
          <Form.Label>Jahr:</Form.Label>
          <Form.Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {years.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {isExcluded ? (
        <div className="mt-3" style={{ fontSize: "0.95rem", color: "#555" }}>
          Für diese Entität liegen keine auswertbaren TNM-Klassifikationsangaben vor.
        </div>
      ) : (
        <>
          {/* Charts */}
          <Row>
            <Col md={6} className="mb-4">
              <SexChart title="Männer" data={dataMen} stackId="M" />
            </Col>
            <Col md={6} className="mb-4">
              <SexChart title="Frauen" data={dataWomen} stackId="W" />
            </Col>
          </Row>

          {/* Table */}
          <Table
            responsive
            className="zeitverlauf-table text-center align-middle"
            style={{ maxWidth: "1000px" }}
          >
            <thead>
              <tr>
                <th className="text-start">TNM-Kategorie</th>
                <th colSpan={3}>Anzahl</th>
                <th colSpan={3}>Anteil in %</th>
              </tr>
              <tr>
                <th className="text-start"></th>
                <th>Gesamt</th>
                <th>Männer</th>
                <th>Frauen</th>
                <th>Gesamt</th>
                <th>Männer</th>
                <th>Frauen</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r, i) =>
                r.section ? (
                  <tr key={`sec-${i}`} className="table-secondary">
                    <td colSpan={7} className="fw-bold text-start">
                      {r.section}
                    </td>
                  </tr>
                ) : (
                  <tr key={`row-${i}`}>
                    <td className="text-start">{r.label}</td>
                    <td>{fmtInt(r.n_g)}</td>
                    <td>{fmtInt(r.n_m)}</td>
                    <td>{fmtInt(r.n_w)}</td>
                    <td>{fmtPct(r.pct_g)}</td>
                    <td>{fmtPct(r.pct_m)}</td>
                    <td>{fmtPct(r.pct_w)}</td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
        </>
      )}
    </Container>
  );
}
